// Accent colours an Organization's dashboard banner can be painted with.
//
// Every entry is chosen to stay legible against the dark dashboard surface
// (#0C1421), since the banner gradient fades that base into this colour.
//
// The API keeps an identical list in `src/constants/brand-palette.ts`.
// **Order is load-bearing**: `pickBrandColor` indexes into it, so both copies must
// stay in the same sequence — otherwise an Organization seeded server-side renders
// one colour and the client fallback picks another for the same id.

export interface BrandColor {
  name: string
  hex: string
}

export const BRAND_PALETTE: BrandColor[] = [
  { name: 'Electric Lime', hex: '#ACFF35' },
  { name: 'Neon Mint', hex: '#2AFFA3' },
  { name: 'Sky Blue', hex: '#38BDF8' },
  { name: 'Electric Blue', hex: '#3B82F6' },
  { name: 'Indigo', hex: '#818CF8' },
  { name: 'Violet', hex: '#A78BFA' },
  { name: 'Hot Pink', hex: '#F472B6' },
  { name: 'Rose', hex: '#FB7185' },
  { name: 'Coral', hex: '#FF6B6B' },
  { name: 'Amber', hex: '#FBBF24' },
  { name: 'Orange', hex: '#F97316' },
  { name: 'Peach', hex: '#FF9E6B' },
  { name: 'Gold', hex: '#EAB308' },
  { name: 'Teal', hex: '#14B8A6' },
  { name: 'Cyan', hex: '#22D3EE' },
  { name: 'Lavender', hex: '#C4B5FD' }
]

/** True for a `#RRGGBB` string. Guards every value that reaches a CSS colour slot. */
export const isValidHex = (value?: string | null): value is string =>
  typeof value === 'string' && /^#[0-9A-Fa-f]{6}$/.test(value)

/**
 * Deterministic palette pick for a seed (an Organization id) — mirrors the API's
 * `pickBrandColor`, so an Organization looks the same whether the colour was
 * seeded server-side or fell back to this.
 */
export const pickBrandColor = (seed: number): string =>
  BRAND_PALETTE[Math.abs(Math.trunc(seed)) % BRAND_PALETTE.length].hex
